'use client';

import { CloseButton, Drawer, Portal } from '@chakra-ui/react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { MdFilterList } from 'react-icons/md';
import Show from '@/components/Molecules/Show';
import FaceliftIconButton from '../FaceliftIconButton';
import type {
	CustomDrawerBackdropProps,
	CustomDrawerBodyProps,
	CustomDrawerCloseTriggerProps,
	CustomDrawerContentProps,
	CustomDrawerFooterProps,
	CustomDrawerHeaderProps,
	CustomDrawerPositionerProps,
	CustomDrawerTitleProps,
	CustomDrawerTriggerProps,
	DrawerOpenChangeDetails,
	FaceliftDrawerProps,
} from './FaceliftDrawer.types';

const CustomDrawer = {
	Backdrop: (props: CustomDrawerBackdropProps) => (
		<Drawer.Backdrop {...props} />
	),
	Positioner: (props: CustomDrawerPositionerProps) => (
		<Drawer.Positioner zIndex={9999} {...props} />
	),
	Content: (props: CustomDrawerContentProps) => <Drawer.Content {...props} />,
	Header: (props: CustomDrawerHeaderProps) => <Drawer.Header {...props} />,
	Title: (props: CustomDrawerTitleProps) => <Drawer.Title {...props} />,
	Body: (props: CustomDrawerBodyProps) => <Drawer.Body {...props} />,
	Footer: (props: CustomDrawerFooterProps) => <Drawer.Footer {...props} />,
	CloseTrigger: (props: CustomDrawerCloseTriggerProps) => (
		<Drawer.CloseTrigger {...props} />
	),
	Trigger: (props: CustomDrawerTriggerProps) => <Drawer.Trigger {...props} />,
};

const FaceliftDrawer = forwardRef(({
	title,
	trigger,
	children,
	bodyContent,
	footer,
	showCloseBtn = true,
	bg,
	color,
	sx,
	defaultValues = [],
	onClear,
	...rest
}: FaceliftDrawerProps, ref) => {
	const [isOpen, setIsOpen] = useState(false);

	// 👇 expose functions to parent via ref
	useImperativeHandle(ref, () => ({
		openDrawer: () => setIsOpen(true),
		closeDrawer: () => setIsOpen(false),
	}));

	const totalSelectedCount = defaultValues?.reduce(
		(acc, item) => acc + (item.options?.length || 0),
		0
	);
	const selectedCategoryNames = defaultValues
		?.filter((item) => item.options?.length > 0)
		.map((item) => item.name);

	const hasSelection = totalSelectedCount > 0;


	return (
		<Drawer.Root
			open={isOpen}
			onOpenChange={(e: DrawerOpenChangeDetails) => setIsOpen(e.open)}
			{...rest}
		>
			<CustomDrawer.Trigger>
				<Show when={trigger !== undefined}>{trigger}</Show>
				<Show when={trigger === undefined}>
					<FaceliftIconButton
						bg={hasSelection ? 'brand.solidGradient' : "transparent"}
						color={hasSelection ? 'white' : "inherit"}
						border="1px solid"
						_hover={{ bg: 'brand.solidGradient', color: 'white' }}
						title='More filters'
						px={2}
					>
						<MdFilterList />
						<Show when={hasSelection}>
							{selectedCategoryNames.length > 1 ? `${totalSelectedCount} Items` : `${totalSelectedCount} ${selectedCategoryNames[0]}`}
							<CloseButton
								size={'xs'}
								width="16px"
								height="16px"
								minWidth="16px"
								bg={{ _dark: 'surfaceGrey', _light: 'white' }}
								_icon={{
									boxSize: 2,
									width: "10px",
									height: "10px"
								}}
								rounded={'full'}
								_hover={{ bg: { _dark: 'surfaceGrey', _light: 'whiteAlpha.700' } }}
								onClick={onClear}
							/>
						</Show>
					</FaceliftIconButton>
				</Show>
			</CustomDrawer.Trigger>
			<Portal>
				<CustomDrawer.Backdrop />
				<CustomDrawer.Positioner>
					<CustomDrawer.Content
						boxShadow="0px 4px 4px 0px #00000040"
						bg={bg}
						color={color}
						style={sx}
					>
						<Show when={title !== undefined}>
							<CustomDrawer.Header gap={0} p={0} m={1} mt={2.5}>
								<FaceliftIconButton
									bg="transparent"
									color="inherit"
									_hover={{ bg: 'transparent' }}
									size="md"
									minW={8}
									cursor={'disabled'}
								>
									<MdFilterList />
								</FaceliftIconButton>
								<CustomDrawer.Title>{title}</CustomDrawer.Title>
							</CustomDrawer.Header>
						</Show>

						<Show when={showCloseBtn}>
							<CustomDrawer.CloseTrigger>
								<CloseButton
									size="sm"
									position="absolute"
									top={2}
									right={0.5}
								/>
							</CustomDrawer.CloseTrigger>
						</Show>

						<Show when={bodyContent !== undefined}>{bodyContent}</Show>
						<CustomDrawer.Body>{children}</CustomDrawer.Body>

						<Show when={footer !== undefined}>
							<CustomDrawer.Footer>{footer}</CustomDrawer.Footer>
						</Show>
					</CustomDrawer.Content>
				</CustomDrawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
});

export default FaceliftDrawer;
